import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { SuspensionChartModel } from 'src/app/models/charts/rental/suspension-chart.model';
import { SuspensionPrintModel } from 'src/app/models/rental/suspension-print-model';
import { SuspensionModel } from 'src/app/models/rental/suspension.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoiceSuspensionTableModel } from 'src/app/models/tables/rental/invoice-suspension-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})

export class SuspensionService {

  constructor(private _httpClient: HttpClient) { }

  annulSuspension(suspensionId: number): Observable<SuspensionModel> {
    return this._httpClient.put<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.AnnulSuspension}/${suspensionId}`,
      null);
  }

  blockSuspension(suspensionId: number): Observable<SuspensionModel> {
    return this._httpClient.put<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.BlockSuspension}/${suspensionId}`,
      null);
  }

  createSuspension(suspensionContract: SuspensionModel): Observable<SuspensionModel> {
    return this._httpClient.post<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.CreateSuspension}`,
      suspensionContract);
  }

  deleteSuspension(suspensionId: number): Observable<SuspensionModel> {
    return this._httpClient.delete<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.DeleteSuspension}/${suspensionId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportInvoiceForSuspensionConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.ExportInvoiceForSuspensionConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getAllCostCentersForSelectAsync(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetAllCostCentersForSelect}`,
      { params: params });
  }

  getAllInvoiceForSuspensionsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<InvoiceSuspensionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<InvoiceSuspensionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetAllInvoiceForSuspensionsWithPagination}`,
      filters, { params: params }
    );
  }

  getAllSuspensionsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetAllSuspensionsWithPagination}`,
      filters, { params: params });
  }

  getAllClientsForSelectAsync(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetAllClientsForSelectAsync}`,);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetColumnsForGeneralConsultation}`);
  }

  getColumnsForInvoiceConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetColumnsForInvoiceConsultationAsync}`);
  }

  getCostCenterByIdForRegisterSuspension(costCenterId: number): Observable<any> {
    const params = new HttpParams().set('costCenterId', costCenterId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetCostCenterByIdForRegisterSuspension}`,
      { params: params });
  }

  getRentalPeriodForRegisterSuspension(rentalPeriodId: number): Observable<any> {
    const params = new HttpParams().set('rentalPeriodId', rentalPeriodId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetRentalPeriodForRegisterSuspension}`,
      { params: params });
  }

  getEmptySuspensionPrint(): Observable<SuspensionPrintModel> {
    return this._httpClient.get<SuspensionPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetEmptySuspensionPrint}`);
  }

  getFirstSuspensionByKeysAsync(branchId: number): Observable<SuspensionModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetFirstSuspensionByKeys}`,
      { params: params });
  }

  getIndicatorsForGeneralConsultation(filters: ColumnResponseModel[]): Observable<SuspensionChartModel> {
    return this._httpClient.post<SuspensionChartModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetIndicatorsForGeneralConsultationAsync}`,
      filters
    );
  }

  getLastSuspensionByKeysAsync(branchId: number): Observable<SuspensionModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetLastSuspensionByKeys}`,
      { params: params });
  }

  getNextSuspensionByKeysAsync(branchId: number, consecutive: number): Observable<SuspensionModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetNextSuspensionByKeys}`,
      { params: params });
  }

  getPermissionPrint(): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetPermissionPrint}`);
  }

  getPreviusSuspensionByKeysAsync(branchId: number, consecutive: number): Observable<SuspensionModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetPreviusSuspensionByKeys}`,
      { params: params });
  }

  getSuspensionById(suspensionId: number): Observable<SuspensionModel> {
    return this._httpClient.get<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetSuspensionById}/${suspensionId}`);
  }

  getSuspensionByKeysAsync(branchId: number, consecutive: number): Observable<SuspensionModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetSuspensionByKeys}`,
      { params: params });
  }

  getSuspensionByKeysByPrintAsync(branchId: number, consecutive: number, suspensionId: number): Observable<SuspensionPrintModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive).set('suspensionId', suspensionId);
    return this._httpClient.get<SuspensionPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.GetSuspensionByKeysByPrintAsync}`,
      { params }
    );
  }

  setSuspensionState(suspensionId: number): Observable<SuspensionModel> {
    return this._httpClient.put<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.SetSuspensionState}/${suspensionId}`, null);
  }

  updateSuspension(suspensionContract: SuspensionModel): Observable<SuspensionModel> {
    return this._httpClient.put<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.UpdateSuspension}`, suspensionContract);
  }

  printSuspensionAsync(suspensionId: number): Observable<SuspensionModel> {
    return this._httpClient.put<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.suspension.PrintSuspensionAsync}/${suspensionId}`, null);
  }
}
